/**
 * LaTeX document generation.
 *
 * These are pure string builders so that the ingest pipeline, the render
 * service and the zip export all produce byte-identical documents.
 */

import { findTypstBlocks } from './typstscan.js';
import { texSafe } from './textutil.js';
import type { SheetDoc, SheetItem } from './index.js';

export const GENERATED_BANNER = '% Generated by Sheet Studio — do not edit by hand.';

/**
 * TikZ libraries and packages that individual exercises rely on but that the
 * shared headers do not load.  Historically these were declared in the
 * preamble of the sheet that used the exercise, so a standalone render of the
 * exercise needs them too.
 */
export const DEFAULT_EXTRA_PREAMBLE = [
  '\\usepackage{mathpartir}',
  '\\usepackage{wrapfig}',
  '\\usepackage{multirow}',
  '\\usetikzlibrary{arrows,arrows.meta,calc,chains,decorations.pathreplacing,fit,matrix,positioning,shadings,shadows,shapes,shapes.arrows,shapes.symbols,trees}',
];

export interface StandaloneOptions {
  /** value for `\pathToRoot`, must end with a slash */
  pathToRoot: string;
  /** header to input, e.g. `tutorialSheetHeader` */
  header: string;
  /**
   * Directory holding the header, relative to `\pathToRoot`.  Defaults to
   * `headers`, which is where every year but 2023's exercise sheets keeps them.
   */
  headerDir?: string;
  /** value for `\sheetid` (used by the printed exercise number) */
  sheetId: string;
  /** the exercise counter is set to this, so the first exercise prints index+1 */
  counterStart?: number;
  /** include `answer` environments */
  solution?: boolean;
  /** render camels in colour */
  coloredCamels?: boolean;
  /** preamble lines copied from the sheet the exercise came from */
  sheetPreamble?: string;
  /** `\usepackage`/`\usetikzlibrary` hoisted out of the exercise body */
  hoisted?: string[];
  /** value for `\pathToMe`, if the code uses it */
  pathToMe?: string | null;
  /**
   * Drop the page header/footer.  A standalone exercise is a fragment, so a
   * page counter is meaningless and it gets in the way of a tight crop.
   */
  plainPages?: boolean;
  body: string;
}

export function buildStandalone(opts: StandaloneOptions): string {
  const lines: string[] = [GENERATED_BANNER];
  lines.push(`\\def\\pathToRoot{${opts.pathToRoot}}`);
  if (opts.pathToMe) lines.push(`\\def\\pathToMe{${opts.pathToMe}}`);
  lines.push(`\\def\\sheetid{${opts.sheetId}}`);
  if (opts.solution) lines.push('\\def\\issolution{}');
  if (opts.coloredCamels) lines.push('\\def\\coloredcamel{}');
  lines.push(`\\input{\\pathToRoot ${opts.headerDir ?? 'headers'}/${opts.header}}`);
  lines.push('');
  lines.push('% --- extra preamble -------------------------------------------------');
  for (const p of DEFAULT_EXTRA_PREAMBLE) lines.push(`\\IfPackageLoadedTF{${packageName(p)}}{}{${p}}`);
  for (const h of opts.hoisted ?? []) lines.push(h);
  if (opts.sheetPreamble?.trim()) {
    lines.push('% --- preamble inherited from the original sheet ---------------------');
    lines.push(opts.sheetPreamble.trim());
  }
  lines.push('');
  lines.push('\\begin{document}');
  if (opts.plainPages) lines.push('\\pagestyle{empty}\\thispagestyle{empty}');
  if (opts.counterStart !== undefined) {
    lines.push(`\\setcounter{ExerciseCounter}{${opts.counterStart}}`);
  }
  lines.push('');
  lines.push(opts.body.trim());
  lines.push('');
  lines.push('\\end{document}');
  return lines.join('\n') + '\n';
}

/**
 * `\IfPackageLoadedTF` only makes sense for `\usepackage`; for anything else we
 * emit the directive unconditionally by returning a name that is never loaded.
 */
function packageName(directive: string): string {
  const m = /^\\usepackage(?:\[[^\]]*\])?\{([^}]*)\}/.exec(directive);
  return m ? m[1] : '@studio-always';
}

/* ----------------------------------------------------------- sheet documents */

export interface SheetTexOptions {
  doc: SheetDoc;
  pathToRoot: string;
  /** resolves an exercise id to the `\input` target used on the sheet */
  inputPathFor: (item: SheetItem) => string | null;
  /** inline body for items that were detached from the corpus */
  solution?: boolean;
  /** emitted verbatim right before `\begin{document}` */
  extraPreamble?: string[];
  /**
   * Signature of the head command in the header pack actually being used.  Head
   * commands disagree: `\tutorialhead` accepts `[kind]{label}{title}` while
   * `\klausurtraininghead` takes no optional argument and `\examtitle` takes
   * five mandatory ones.  Emitting the wrong shape is a hard LaTeX error, so the
   * caller resolves the real signature from the pack and passes it here.
   */
  headSpec?: { arity: number; optional: boolean };
  /**
   * Lines that make the header available, replacing the default single
   * `\input{…}` — custom header packs input a builtin and then append their own
   * definitions.
   */
  headerLines?: string[];
  /** used to fill the course argument of exam-style heads */
  courseLine?: string;
}

/**
 * The mandatory arguments to pass to a head command.
 *
 * Two-argument heads take `{label}{title}`, which covers every tutorial and
 * exam-prep sheet.  Five-argument `\examtitle` takes
 * `{title}{course}{date}{minutes}{points}` and never sees the label.  Anything
 * else is padded so the call is at least well-formed.
 */
export function headArgsFor(
  meta: SheetDoc['meta'],
  arity: number,
  courseLine = '',
): string[] {
  /*
   * An empty stored argument means "derive it", always. Entering an exam date used to
   * store the whole list — title as it was at that moment, an empty course line — and a
   * stored list was returned verbatim, so the course line vanished and later title
   * changes never reached the head.
   */
  const derived =
    arity === 2 ? [meta.label, meta.title] : arity === 5 ? [meta.title, courseLine, '', '', ''] : [meta.label, meta.title];
  if (meta.headArgs?.length) {
    const a = meta.headArgs.slice(0, arity);
    while (a.length < arity) a.push('');
    return a.map((value, i) => (value.trim() ? value : (derived[i] ?? '')));
  }
  if (arity === 2) return [meta.label, meta.title];
  if (arity === 5) return [meta.title, courseLine, '', '', ''];
  const a = [meta.label, meta.title];
  while (a.length < arity) a.push('');
  return a.slice(0, arity);
}

const HEAD_FOR_KIND: Record<string, string> = {
  tutorial: 'tutorialhead',
  exercise: 'exercisehead',
  'exam-prep': 'klausurtraininghead',
  'mock-exam': 'tutorialhead',
  cards: 'tutorialhead',
  other: 'tutorialhead',
};

export function headerForKind(kind: string): string {
  if (kind === 'mock-exam') return 'examHeader';
  if (kind === 'exercise') return 'exerciseSheetHeader';
  return 'tutorialSheetHeader';
}

/**
 * The header pack a sheet compiles against: an explicit choice when the author
 * made one, otherwise the historical mapping from `kind`.
 */
export function headerForSheet(meta: SheetDoc['meta']): string {
  return meta.headerName?.trim() || headerForKind(meta.kind);
}

export function buildSheetTex(opts: SheetTexOptions): string {
  const { doc } = opts;
  const head = doc.meta.headCommand || HEAD_FOR_KIND[doc.meta.kind] || 'tutorialhead';
  const lines: string[] = [
    '% !TEX program = pdflatex',
    GENERATED_BANNER,
    `\\def\\pathToRoot{${opts.pathToRoot}}`,
  ];
  if (opts.solution) lines.push('\\def\\issolution{}');
  if (doc.meta.coloredCamels) lines.push('\\def\\coloredcamel{}');
  if (opts.headerLines?.length) lines.push(...opts.headerLines);
  else lines.push(`\\input{\\pathToRoot headers/${headerForSheet(doc.meta)}}`);
  lines.push('');
  for (const p of DEFAULT_EXTRA_PREAMBLE) lines.push(`\\IfPackageLoadedTF{${packageName(p)}}{}{${p}}`);
  for (const p of opts.extraPreamble ?? []) lines.push(p);
  // `\sheettype` is fixed by each header pack (T for tutorials, A for auxiliary
  // tutorials).  Overriding it after the input is what lets one pack serve
  // sheets whose exercises should print as TB.1, XB.1, B.1, …
  if (doc.meta.sheetType !== undefined && doc.meta.sheetType !== null) {
    lines.push(`\\renewcommand{\\sheettype}{${doc.meta.sheetType}}`);
  }
  lines.push('');
  lines.push('\\begin{document}');
  lines.push('');
  const spec = opts.headSpec ?? { arity: 2, optional: head !== 'klausurtraininghead' };
  const args = headArgsFor(doc.meta, spec.arity, opts.courseLine ?? '')
    .map((a) => `{${a}}`)
    .join('');
  // Only heads that declare an optional argument may receive one; passing
  // `[…]` to `\klausurtraininghead` (which is `\newcommand[2]`) shifts every
  // argument and breaks the compile.
  const headOpt = spec.optional && doc.meta.headKindLabel ? `[${doc.meta.headKindLabel}]` : '';
  lines.push(`\\${head}${headOpt}${args}`);
  if (doc.meta.subtitle.trim()) {
    lines.push('');
    lines.push(doc.meta.subtitle.trim());
  }
  if (doc.meta.numberOffset) {
    lines.push(`\\setcounter{ExerciseCounter}{${doc.meta.numberOffset}}`);
  }
  lines.push('');

  for (const item of doc.items) {
    switch (item.kind) {
      case 'pagebreak':
        lines.push('\\clearpage');
        lines.push('');
        break;
      case 'heading':
        lines.push(`\\section*{${item.body ?? ''}}`);
        lines.push('');
        break;
      case 'text':
        lines.push(item.body ?? '');
        lines.push('');
        break;
      case 'exercise': {
        if (item.note) lines.push(`% ${item.note.replace(/\n/g, ' ')}`);
        if (item.numberOverride) {
          lines.push(`% numbering overridden to ${item.numberOverride}`);
        }
        const inlined = !opts.inputPathFor(item);
        if (inlined) {
          lines.push(applyOverrides(item, item.body ?? ''));
        } else {
          const target = opts.inputPathFor(item)!;
          if (item.numberOverride) {
            // the number lives in the exercise environment's arguments, so a renumbered
            // exercise has to be inlined rather than \input
            lines.push(applyOverrides(item, item.body ?? ''));
          } else {
            lines.push(`\\input{${target}}`);
          }
        }
        lines.push('');
        break;
      }
    }
  }
  lines.push('\\end{document}');
  return lines.join('\n') + '\n';
}

/**
 * Rewrite the argument list of the first `exercise` environment in `code` so that the
 * sheet's number and the exercise's heading on sheets take effect. (Camels and points are
 * the exercise's own: a sheet that wants others uses a variant of the exercise.)
 */
export function applyOverrides(
  item: Pick<SheetItem, 'numberOverride'>,
  code: string,
  opts: { heading?: string; hidePoints?: boolean } = {},
): string {
  const marker = '\\begin{exercise}';
  const at = code.indexOf(marker);
  if (at < 0) return code;
  const parsed = scanExerciseArgs(code, at + marker.length);
  const number = item.numberOverride ?? parsed.number;
  /*
   * A heading typed into a field is made safe — an `&` or `_` in it was an error — and
   * braced when it holds a `]`, which would otherwise end the optional argument early.
   * The title written in the exercise's own source is LaTeX already and kept as it is.
   */
  const title = opts.heading ? texSafe(opts.heading) : parsed.title;
  const difficulty = parsed.difficulty;
  // a sheet that prints no points (`SheetMeta.hidePoints`) drops them from its copy
  const points = opts.hidePoints ? null : parsed.points;
  let args = '';
  if (number) args += `=${number}=`;
  if (title) args += title.includes(']') ? `[{${title}}]` : `[${title}]`;
  if (parsed.competencies) args += `/${parsed.competencies}/`;
  if (difficulty != null) args += `<${difficulty}>`;
  if (points != null) args += `$${points}$`;
  return code.slice(0, at) + marker + args + code.slice(parsed.end);
}

interface ScannedArgs {
  number: string | null;
  title: string | null;
  competencies: string | null;
  difficulty: number | null;
  points: number | null;
  end: number;
}

/** Minimal re-implementation of the argument scan, used for overrides. */
export function scanExerciseArgs(code: string, from: number): ScannedArgs {
  const res: ScannedArgs = {
    number: null,
    title: null,
    competencies: null,
    difficulty: null,
    points: null,
    end: from,
  };
  let i = from;
  const skip = () => {
    while (i < code.length && /\s/.test(code[i])) i++;
  };
  const take = (open: string, close: string): string | null => {
    const save = i;
    skip();
    if (code[i] !== open) {
      i = save;
      return null;
    }
    let depth = 1;
    let j = i + 1;
    while (j < code.length) {
      if (code[j] === '\\') {
        j += 2;
        continue;
      }
      if (open !== close && code[j] === open) depth++;
      else if (code[j] === close) {
        depth--;
        if (depth === 0) break;
      }
      j++;
    }
    if (j >= code.length) {
      i = save;
      return null;
    }
    const value = code.slice(i + 1, j);
    i = j + 1;
    return value;
  };
  const num = take('=', '=');
  if (num !== null && /^[\w.\s-]{1,12}$/.test(num)) res.number = num.trim();
  else if (num !== null) i = from;
  const title = take('[', ']');
  if (title !== null) res.title = title.trim();
  const comp = take('/', '/');
  if (comp !== null && /^[\w\s,.-]{1,60}$/.test(comp)) res.competencies = comp.trim();
  const diff = take('<', '>');
  if (diff !== null && /^\d$/.test(diff.trim())) res.difficulty = Number(diff.trim());
  const pts = take('$', '$');
  if (pts !== null && /^\d+(\.\d+)?$/.test(pts.trim())) res.points = Number(pts.trim());
  res.end = i;
  return res;
}

/**
 * Split a body into the exercise and its sample solution.
 *
 * The editor stores one string per item, with the solution embedded in it —
 * `\begin{answer}…\end{answer}` in LaTeX, `#answer[…]` in Typst. That is the right
 * storage (it is what a sheet inputs), but it left no way to *edit* a solution: the
 * inspector offered a checkbox to include it and a single box containing the whole
 * exercise, so finding the answer meant scrolling through the LaTeX of a task that may
 * run to a page.
 *
 * Returns the three pieces so the caller can edit them separately and put them back:
 * everything before the answer, the answer's own contents, and everything after. `answer`
 * is null when there is none, which is how the caller knows to offer "add one".
 */
export function splitSolution(
  body: string,
  markup: 'latex' | 'typst' = 'latex',
): { before: string; answer: string | null; after: string; name?: 'answer' | 'solution' } {
  if (markup === 'typst') {
    /*
     * The same scanner the student-version stripping uses, so the two cannot disagree —
     * and the same two names: course-template items keep their solution in
     * `#solution[…]`. Looking for `#answer` only offered "Add a solution" on those, which
     * appended a second, empty block next to the real one.
     */
    const block = findTypstBlocks(body, ['answer', 'solution'])[0];
    if (!block || block.close < 0) return { before: body, answer: null, after: '' };
    return {
      before: body.slice(0, block.start),
      answer: body.slice(block.open + 1, block.close),
      after: body.slice(block.close + 1),
      name: block.name === 'solution' ? 'solution' : 'answer',
    };
  }

  const m = /\\begin\{answer\}([\s\S]*?)\\end\{answer\}/.exec(body);
  if (!m) return { before: body, answer: null, after: '' };
  return {
    before: body.slice(0, m.index),
    answer: m[1] ?? '',
    after: body.slice(m.index + m[0].length),
  };
}

/**
 * Put the three pieces back together. `answer: null` removes the solution; an empty
 * string keeps an empty one.
 *
 * Emptiness used to mean removal too, which is why "Add one" did nothing: it asked for a
 * blank answer, got the wrapper dropped, and the editor still said there was none. Same
 * for clearing a solution's text while rewriting it — the box vanished mid-edit.
 */
export function joinSolution(
  parts: { before: string; answer: string | null; after: string; name?: 'answer' | 'solution' },
  markup: 'latex' | 'typst' = 'latex',
): string {
  const { before, answer, after } = parts;
  if (answer === null) return (before + after).trimEnd() + '\n';
  const wrapped =
    markup === 'typst'
      ? `#${parts.name ?? 'answer'}[${answer}]`
      : `\\begin{answer}${answer}\\end{answer}`;
  return before + wrapped + after;
}
